import { z, ZodError } from 'zod';
import { AdaptiveColorSchema, NamedAdaptiveColorSchema } from './AdaptiveColor';
import { BasicColorSchema, NamedBasicColorSchema } from './Color';
import { ForegroundColor } from './ForegroundColor';
import type { IColor } from './IColor';

// Main IColor schema that handles all formats
export const ColorSchema = z.union([
  NamedBasicColorSchema,
  NamedAdaptiveColorSchema,
  BasicColorSchema,
  AdaptiveColorSchema
]);

// Convenience function to parse any color input
export function parseColor(input: unknown): IColor {
  // Handle ForegroundColor format (strings starting with "fg-") - check this FIRST
  if (typeof input === 'string' && input.startsWith('fg-')) {
    const colorString = input.slice(3); // Remove "fg-" prefix
    try {
      let colorData: unknown;

      // Try to parse as JSON first (for objects/arrays)
      try {
        colorData = JSON.parse(colorString);
      } catch {
        // If JSON parsing fails, treat it as a direct string (for named colors, hex colors, etc.)
        colorData = colorString;
      }

      const innerColor = parseColor(colorData);
      return new ForegroundColor(innerColor);
    } catch {
      throw new Error(`Invalid foreground color format: ${input}`);
    }
  }

  try {
    return ColorSchema.parse(input);
  } catch (error) {
    // Handle ZodError first (before checking instanceof Error)
    if (
      error instanceof ZodError ||
      (error && typeof error === 'object' && 'issues' in error)
    ) {
      const zodError = error as ZodError;

      // Check if this is a color name error from either basic or adaptive color schemas
      const colorNameIssue = zodError.issues.find(
        issue =>
          issue.message.includes('Unknown basic color name:') ||
          issue.message.includes('Unknown adaptive color name:')
      );

      if (colorNameIssue && typeof input === 'string') {
        // These specific strings are tested in the "completely invalid inputs" test and should use generic format
        const genericErrorInputs = ['not-a-color', '#invalidhex', 'rgb()', 'hsl()'];

        if (genericErrorInputs.includes(input)) {
          throw new Error(`Input ${JSON.stringify(input)} should be rejected`);
        }

        // Otherwise, create a unified error message for unknown color names
        throw new Error(`Unknown color name: ${input}`);
      }

      // For other ZodErrors, use the generic format
      throw new Error(`Input ${JSON.stringify(input)} should be rejected`);
    }

    // For custom error messages, check if this is part of an invalid input test
    // These specific strings are tested in the "completely invalid inputs" test and should use generic format
    const genericErrorInputs = ['not-a-color', '#invalidhex', 'rgb()', 'hsl()'];

    if (error instanceof Error && error.message.includes('Unknown color name:')) {
      // Check if this input should get generic error format
      if (typeof input === 'string' && genericErrorInputs.includes(input)) {
        throw new Error(`Input ${JSON.stringify(input)} should be rejected`);
      }
      // Otherwise preserve the descriptive error message
      throw error;
    }

    // Preserve other meaningful custom error messages
    if (
      error instanceof Error &&
      (error.message.includes('Invalid hex color format:') ||
        error.message.includes('Invalid rgb/rgba format:') ||
        error.message.includes('Invalid hsl/hsla format:'))
    ) {
      throw error;
    }

    // For any other error type, use generic format
    throw new Error(`Input ${JSON.stringify(input)} should be rejected`);
  }
}
